import React, { useCallback, useEffect, useState } from 'react';
import { AiOutlineCalendar, AiOutlineFilePdf, AiOutlineInfoCircle } from 'react-icons/ai';
import { FaReceipt, FaCreditCard, FaMoneyCheckAlt } from 'react-icons/fa';
import { BsCurrencyDollar } from 'react-icons/bs';
import { useAppDispatch } from '../../../store';
import { getTransactionDetails } from '../../../store/actions/SubscriptionActions';
import Skeleton from '../TailwindComponents/Skeleton';
import { useTranslation } from 'react-i18next';
import { convertLongDateWithTranslation } from '../DateConverter';

interface TransactionDetailsProps {
    AmountPaid: number;
    Currency: string;
    InvoiceID: string;
    InvoiceURL: string;
    InvoicePDF: string;
    PaymentID: string;
    PaymentStatus: string;
    PaymentMethod: {
        "Type": string,
        "Last4": string,
        "Brand": string
    };
    ReceiptURL: string;
    CreatedAt: string;
}

const TransactionDetails: React.FC<{ transactionId: string }> = ({ transactionId }) => {
    const [invoiceData, setInvoiceData] = useState<TransactionDetailsProps | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const fetchTransactionDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await dispatch(getTransactionDetails(transactionId));
            if (response) {
                setInvoiceData(response);
            }
        } catch (err) {
            console.error("Failed to fetch transaction details", err);
        } finally {
            setLoading(false);
        }
    }, [dispatch, transactionId]);

    useEffect(() => {
        fetchTransactionDetails();
    }, [fetchTransactionDetails]);

    const formatDate = (dateString: string) => {
        const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    if (loading && !invoiceData) {
        return <Skeleton variant="pane" />;
    }

    return (
        <div className="w-full h-full mx-auto p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-medium text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <AiOutlineInfoCircle className="text-info-500" /> {t("Invoice details")}
            </h2>

            <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                    <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <BsCurrencyDollar className="text-success-500" /> {t("Amount paid")}:
                    </span>
                    <span className="text-success-600 dark:text-success-400 font-medium">
                        ${invoiceData?.AmountPaid} {invoiceData?.Currency}
                    </span>
                </div>

                <div className="flex justify-between items-center border-b pb-2">
                    <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <AiOutlineInfoCircle className="text-warning-500" /> {t("Payment status")}:
                    </span>
                    <span className={invoiceData?.PaymentStatus === 'succeeded' ? 'text-success-500 font-medium' : 'text-danger-500 font-medium'}>
                        {invoiceData?.PaymentStatus ? t(invoiceData?.PaymentStatus).toUpperCase() : ""}
                    </span>
                </div>

                <div className="flex justify-between items-center border-b pb-2">
                    <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <FaCreditCard className="text-info-500" /> {t("Payment method")}:
                    </span>
                    {invoiceData?.PaymentMethod.Type === 'card' ? (
                        <span className="flex items-center gap-2 text-gray-900 dark:text-white">
                            {invoiceData?.PaymentMethod.Brand?.toUpperCase()} •••• {invoiceData?.PaymentMethod.Last4}
                        </span>
                    ) : (
                        <span className="text-gray-500">{t("N/A")}</span>
                    )}
                </div>

                <div className="flex justify-between items-center border-b pb-2">
                    <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1">
                        <AiOutlineCalendar className="text-purple-500" /> {t("Created at")}:
                    </span>
                    <span className="text-gray-900 dark:text-white">{invoiceData?.CreatedAt && convertLongDateWithTranslation(formatDate(invoiceData?.CreatedAt))}</span>
                </div>

                <div className="mb-0 flex flex-col gap-3 mt-4">
                    <a
                        href={invoiceData?.InvoicePDF}
                        download={`Invoice_${invoiceData?.InvoiceID}.pdf`}
                        className="flex items-center justify-center gap-2 bg-danger-500 text-white px-4 py-2 rounded-md hover:bg-danger-600 transition"
                    >
                        <AiOutlineFilePdf /> {t("Download invoice")}
                    </a>

                    <a
                        href={invoiceData?.ReceiptURL}
                        download={`Receipt_${invoiceData?.InvoiceID}.pdf`}
                        className="flex items-center justify-center gap-2 bg-success-500 text-white px-4 py-2 rounded-md hover:bg-success-600 transition"
                    >
                        <FaReceipt /> {t("Download receipt")}
                    </a>

                    {invoiceData?.InvoiceURL && (
                        <a
                            href={invoiceData?.InvoiceURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 bg-info-500 text-white px-4 py-2 rounded-md hover:bg-info-600 transition"
                        >
                            <FaMoneyCheckAlt /> {t("View invoice online")}
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransactionDetails;
